import {
  AlertTriangle,
  Boxes,
  Calculator,
  Check,
  ChevronRight,
  Clock,
  Code2,
  Hammer,
  Handshake,
  Layers,
  Rocket,
  ShoppingCart,
  Tv,
  type LucideIcon,
} from "lucide-react";
import { RevealSection } from "@/components/Reveal";
import { SiteLayout } from "@/components/SiteLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

export function BuildVsBuyPage() {
  const { messages, site, href } = useSite();
  const u = (path: string) => localizeUrl(path, site.locale, href);
  const salesCta = u("/contacts?inquiry=sales#inquiry-form");

  const heroStats = [
    { icon: Layers, label: messages.bbStat1 },
    { icon: Clock, label: messages.bbStat2 },
    { icon: Rocket, label: messages.bbStat3 },
  ];

  const layers = [
    { name: messages.bbLayer1Name, body: messages.bbLayer1Body },
    { name: messages.bbLayer2Name, body: messages.bbLayer2Body },
    { name: messages.bbLayer3Name, body: messages.bbLayer3Body },
    { name: messages.bbLayer4Name, body: messages.bbLayer4Body },
    { name: messages.bbLayer5Name, body: messages.bbLayer5Body },
    { name: messages.bbLayer6Name, body: messages.bbLayer6Body },
    { name: messages.bbLayer7Name, body: messages.bbLayer7Body },
    { name: messages.bbLayer8Name, body: messages.bbLayer8Body },
  ];

  const approaches: { icon: LucideIcon; name: string; body: string }[] = [
    { icon: Hammer, name: messages.bbApproach1Name, body: messages.bbApproach1Body },
    { icon: Code2, name: messages.bbApproach2Name, body: messages.bbApproach2Body },
    { icon: Tv, name: messages.bbApproach3Name, body: messages.bbApproach3Body },
    { icon: Handshake, name: messages.bbApproach4Name, body: messages.bbApproach4Body },
  ];

  const whenItems = [messages.bbWhen1, messages.bbWhen2, messages.bbWhen3, messages.bbWhen4];

  const tableRows = [
    { label: messages.bbRow1Label, build: messages.bbRow1Build, buy: messages.bbRow1Buy },
    { label: messages.bbRow2Label, build: messages.bbRow2Build, buy: messages.bbRow2Buy },
    { label: messages.bbRow3Label, build: messages.bbRow3Build, buy: messages.bbRow3Buy },
    { label: messages.bbRow4Label, build: messages.bbRow4Build, buy: messages.bbRow4Buy },
    { label: messages.bbRow5Label, build: messages.bbRow5Build, buy: messages.bbRow5Buy },
    { label: messages.bbRow6Label, build: messages.bbRow6Build, buy: messages.bbRow6Buy },
    { label: messages.bbRow7Label, build: messages.bbRow7Build, buy: messages.bbRow7Buy },
    { label: messages.bbRow8Label, build: messages.bbRow8Build, buy: messages.bbRow8Buy },
    { label: messages.bbRow9Label, build: messages.bbRow9Build, buy: messages.bbRow9Buy },
    { label: messages.bbRow10Label, build: messages.bbRow10Build, buy: messages.bbRow10Buy },
  ];

  const faqs = [
    { q: messages.bbFaqQ1, a: messages.bbFaqA1 },
    { q: messages.bbFaqQ2, a: messages.bbFaqA2 },
    { q: messages.bbFaqQ3, a: messages.bbFaqA3 },
    { q: messages.bbFaqQ4, a: messages.bbFaqA4 },
    { q: messages.bbFaqQ5, a: messages.bbFaqA5 },
    { q: messages.bbFaqQ6, a: messages.bbFaqA6 },
  ];

  return (
    <SiteLayout>
      {/* Breadcrumb */}
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={u("/")} className="hover:text-foreground">
            {messages.guidesBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={u("/guides")} className="hover:text-foreground">
            {messages.navGuides}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <span className="text-foreground">{messages.bbBreadcrumbPage}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div aria-hidden className="bg-grid pointer-events-none absolute inset-0 -z-10 opacity-40" />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 top-0 -z-10 h-[440px] w-[600px] rounded-full opacity-40 blur-3xl"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="mx-auto max-w-3xl px-6 py-16 text-center lg:py-24">
          <div className="inline-flex animate-fade-up items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1 text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            {messages.bbHeroEyebrow}
          </div>
          <h1
            className="mt-4 animate-fade-up text-balance text-4xl font-semibold tracking-tight sm:text-5xl"
            style={{ animationDelay: "80ms" }}
          >
            {messages.bbHeroTitle}{" "}
            <span className="text-gradient">{messages.bbHeroTitleAccent}</span>
          </h1>
          <p
            className="mx-auto mt-6 max-w-2xl animate-fade-up text-pretty text-lg text-muted-foreground"
            style={{ animationDelay: "160ms" }}
          >
            {messages.bbHeroSub}
          </p>
          <p
            className="mt-5 animate-fade-up text-pretty text-sm font-medium text-foreground/80"
            style={{ animationDelay: "220ms" }}
          >
            {messages.bbHeroNote}
          </p>
          <div
            className="mt-8 flex animate-fade-up flex-wrap justify-center gap-3"
            style={{ animationDelay: "280ms" }}
          >
            <Button asChild size="lg" className="w-full sm:w-auto">
              <a href={salesCta}>{messages.bbHeroCtaPrimary}</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
              <a href="#layers">{messages.bbHeroCtaSecondary}</a>
            </Button>
          </div>
          <div
            className="mt-8 flex animate-fade-up flex-wrap justify-center gap-2"
            style={{ animationDelay: "340ms" }}
          >
            {heroStats.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1.5 text-xs text-foreground/90 backdrop-blur"
              >
                <Icon className="h-3.5 w-3.5 text-primary" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Thesis — build rarely means what it sounds like */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.bbThesisEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.bbThesisTitle}
          </h2>
          <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>{messages.bbThesisBody1}</p>
            <p>{messages.bbThesisBody2}</p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-primary/30 bg-card/70 p-5 shadow-glow">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <Boxes className="h-4 w-4" />
                {messages.bbThesisCoreLabel}
              </div>
              <p className="mt-2 text-lg font-semibold text-foreground">
                {messages.bbThesisCoreValue}
              </p>
            </div>
            <div className="rounded-2xl border border-border/60 bg-card/40 p-5">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <ShoppingCart className="h-4 w-4" />
                {messages.bbThesisReachLabel}
              </div>
              <p className="mt-2 text-base font-medium text-foreground/90">
                {messages.bbThesisReachValue}
              </p>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* The 8 layers */}
      <RevealSection id="layers" className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.bbLayersEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.bbLayersTitle}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              {messages.bbLayersIntro}
            </p>
          </div>
          <ol className="mt-10 space-y-3">
            {layers.map((l, i) => (
              <li
                key={l.name}
                className="flex items-start gap-4 rounded-2xl border border-border/60 bg-card/60 p-5"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-sm font-semibold text-primary">
                  {i + 1}
                </span>
                <div>
                  <h3 className="text-base font-semibold text-foreground">{l.name}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{l.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </RevealSection>

      {/* The build spectrum — 4 approaches */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.bbSpectrumEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.bbSpectrumTitle}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {approaches.map(({ icon: Icon, name, body }) => (
              <article key={name} className="rounded-2xl border border-border/60 bg-card/60 p-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="text-base font-semibold text-foreground">{name}</h3>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* The honest cost callout */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="rounded-2xl border border-primary/25 bg-card/70 p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <AlertTriangle className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-primary">
                  {messages.bbCostEyebrow}
                </p>
                <h2 className="mt-2 text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                  {messages.bbCostTitle}
                </h2>
                <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                  {messages.bbCostBody}
                </p>
              </div>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* When to build */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.bbWhenEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.bbWhenTitle}
          </h2>
          <ul className="mt-6 space-y-3">
            {whenItems.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 rounded-xl border border-border/60 bg-card/60 px-4 py-3"
              >
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Check className="h-4 w-4" />
                </span>
                <span className="text-sm text-foreground/90">{item}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 rounded-xl border border-primary/20 bg-primary/[0.04] px-5 py-4 text-sm leading-relaxed text-foreground/90">
            {messages.bbWhenNote}
          </div>
        </div>
      </RevealSection>

      {/* Comparison table */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.bbTableEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.bbTableTitle}
            </h2>
          </div>
          <div className="mt-10 overflow-x-auto rounded-2xl border border-border/60 bg-card/60">
            <table className="w-full min-w-[680px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-muted-foreground">
                    {messages.bbTableColDimension || " "}
                  </th>
                  <th className="px-4 py-3">
                    <span className="inline-flex items-center gap-2 font-semibold text-foreground">
                      <Hammer className="h-4 w-4 text-muted-foreground" />
                      {messages.bbTableColBuild}
                    </span>
                  </th>
                  <th className="px-4 py-3">
                    <span className="inline-flex items-center gap-2 font-semibold text-foreground">
                      <Handshake className="h-4 w-4 text-primary" />
                      {messages.bbTableColBuy}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((r) => (
                  <tr key={r.label} className="border-b border-border/60 align-top last:border-0">
                    <th scope="row" className="px-4 py-3 text-left font-medium text-foreground/90">
                      {r.label}
                    </th>
                    <td className="px-4 py-3 text-muted-foreground">{r.build}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.buy}</td>
                  </tr>
                ))}
                <tr className="bg-primary/[0.06]">
                  <th scope="row" className="px-4 py-3 text-left font-semibold text-foreground">
                    {messages.bbTableInextLabel}
                  </th>
                  <td colSpan={2} className="px-4 py-3 font-medium text-foreground/90">
                    <a
                      href={u("/solutions/customization")}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      {messages.bbTableInextValue}
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">{messages.bbTableNote}</p>
        </div>
      </RevealSection>

      {/* Where we fit */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.bbFitEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.bbFitTitle}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground">
            {messages.bbFitBody}
          </p>
          <div className="mt-8 flex flex-col gap-4 rounded-2xl border border-primary/20 bg-primary/[0.04] p-6 sm:flex-row sm:items-center">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Calculator className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-base font-semibold text-foreground">{messages.bbFitNoteTitle}</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {messages.bbFitNoteBody}
              </p>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* FAQ */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.bbFaqEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.bbFaqTitle}
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map(({ q, a }, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-base font-medium">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </RevealSection>

      {/* Final CTA */}
      <RevealSection className="relative overflow-hidden bg-foreground py-16 text-background sm:py-20">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-0 opacity-30"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-background/70">
            {messages.bbCtaEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.bbCtaTitle}
          </h2>
          <p className="mt-4 text-base text-background/80">{messages.bbCtaBody}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto">
              <a href={salesCta}>{messages.bbCtaPrimary}</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background sm:w-auto"
            >
              <a href={salesCta}>{messages.bbCtaSecondary}</a>
            </Button>
          </div>
        </div>
      </RevealSection>
    </SiteLayout>
  );
}
